import * as Yup from 'yup';
import { useMemo, useEffect, useState, useRef, useCallback, memo } from 'react';
import { ObjectShape } from 'yup/lib/object';
import { FormikTouched, FormikValues, useFormik } from 'formik';
import { FieldData, FormCtx, FormikOptions, InputState, UseFormReturn, TemplateData } from './types';
import Box from '../../Layout/Box/Box';
import ElementContainer from '../../Input/ElementContainer/ElementContainer';
import BodyText from '../../Text/BodyText/BodyText';
import isEqual from 'lodash/isEqual';

const schemaCache = new Map<string, Yup.ObjectSchema<ObjectShape>>();

function hashValidationKey<T>(activeFields: Array<{ fieldName: string; schema?: any }>, values: T): string {
  const fieldKeys = activeFields
    .map((f) => {
      const schemaDesc = f.schema?.describe?.() ?? {};
      return `${f.fieldName}:${JSON.stringify(schemaDesc)}`;
    })
    .sort()
    .join('|');

  // Include relevant values that affect validation

  const relevantValues = Object.entries(values as Record<string, any>)
    .map(([key, val]) => `${key}=${val}`)
    .sort()
    .join('|');

  return `${fieldKeys}::${relevantValues}`;
}

function getValidationSchema<T>(
  ctx: FormCtx<T>,
  fieldDataList: FieldData<T>[],
  initialValidationSchema?: { [key: string]: any },
): Yup.ObjectSchema<ObjectShape> {
  const activeFields = fieldDataList
    .filter(
      (fieldData) =>
        fieldData.validate && fieldData.showField(ctx.values) && fieldData.getInputState(ctx) !== 'disabled',
    )
    .map((fieldData) => ({
      fieldName: fieldData.fieldName,
      schema: fieldData.validate?.(ctx),
    }));

  const cacheKey = hashValidationKey(activeFields, ctx.values);

  if (schemaCache.has(cacheKey)) {
    return schemaCache.get(cacheKey)!;
  }

  const validations = activeFields.reduce(
    (existingValidations, { fieldName, schema }) => ({
      ...existingValidations,
      [fieldName]: schema,
    }),
    { ...initialValidationSchema },
  );

  const schema = Yup.object().shape(validations);
  schemaCache.set(cacheKey, schema);

  // Limit cache size
  if (schemaCache.size > 10) {
    const firstKey = schemaCache.keys().next().value;
    if (firstKey) schemaCache.delete(firstKey);
  }

  return schema;
}

export function RequiredIcon() {
  return (
    <span
      style={{
        color: 'red',
      }}
    >
      *
    </span>
  );
}

// The issue was that ctx changes on every render, so WeakMap cache was never hit
// and handlers need to reference current ctx/fieldOptions anyway
function formikHandlerField<T, K, J>(
  ctx: FormCtxWithReviewing<T>,
  fieldData: FieldData<T>,
  fieldOptions: FormikOptions<K, J>,
  fieldIndex: number,
  completeDataList: FieldData<T>[],
) {
  let combinedTouched: FormikTouched<T> = {};
  const handleFocus = (event: React.FocusEvent<any>) => {
    if (fieldIndex !== -1) {
      const touchedFields: Record<string, boolean> = {};
      for (let i = 0; i < fieldIndex; i++) {
        touchedFields[completeDataList[i].fieldName] = true;
      }
      const combinedTouched = { ...ctx.touched, ...touchedFields };
      ctx.setTouched(combinedTouched);
    }

    if ('onFocus' in fieldOptions && typeof fieldOptions.onFocus === 'function') {
      fieldOptions.onFocus(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<any>) => {
    ctx.handleBlur(event);

    if ('onBlur' in fieldOptions && typeof fieldOptions.onBlur === 'function') {
      fieldOptions.onBlur(event);
    }
  };

  const handleChange = (event: React.ChangeEvent<any>, data?: any) => {
    ctx.handleChange(event);

    if (fieldOptions.onChange) {
      fieldOptions.onChange(event as any, data);
    }
  };

  const hasError = !!(
    ctx.errors[fieldData.fieldName] &&
    (ctx.touched[fieldData.fieldName] || combinedTouched[fieldData.fieldName])
  );
  const errorMessage = hasError ? ctx.errors[fieldData.fieldName]?.toString() : undefined;

  return {
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChange,
    disabled: getVisualInputState(ctx, fieldData) === 'disabled',
    name: String(fieldData.fieldName),
    error: errorMessage || null,
    errorMsg: errorMessage,
    value: fieldOptions.formatValue
      ? fieldOptions.formatValue(ctx.values[fieldData.fieldName] as unknown as J)
      : (ctx.values[fieldData.fieldName] as unknown as K),
  };
}

function getVisualInputState<T>(ctx: FormCtxWithReviewing<T>, fieldData: FieldData<T>): InputState {
  if (ctx.isReviewing || ctx.isSubmitting) return 'disabled';

  return fieldData.getInputState(ctx);
}

export const RenderDefaultFormElement = memo(
  function RenderDefaultFormElement(templateData: TemplateData): React.JSX.Element {
    const showingLabel = templateData.label || templateData.inputState === 'required' || templateData.tooltip;

    return (
      <ElementContainer
        key={`elementContainer_${templateData.fieldName}`}
        label={
          showingLabel ? (
            <Box ai="center" gapSize={templateData.inputState === 'required' ? '2px' : '4px'}>
              {templateData.label && (templateData.label || <span>&nbsp;</span>)}
              {templateData.inputState === 'required' && <RequiredIcon />}
              {templateData.tooltip && templateData.tooltip}
            </Box>
          ) : null
        }
        padding="0px 0px 8px"
        isBold={false}
        input={
          <Box flx="col" gapSize="8px">
            {templateData.template}
            {templateData.helpText && <BodyText size="sm">{templateData.helpText}</BodyText>}
          </Box>
        }
      />
    );
  },
  (prev, next) => {
    return (
      prev.fieldName === next.fieldName &&
      prev.inputState === next.inputState &&
      prev.label === next.label &&
      prev.tooltip === next.tooltip &&
      prev.helpText === next.helpText &&
      prev.template === next.template
    );
  },
);

type FormCtxWithReviewing<T> = FormCtx<T> & { isReviewing: boolean };

export function generateTemplateData<T>(
  ctx: FormCtxWithReviewing<T>,
  fieldData: FieldData<T>,
  fieldIndex: number,
  completeDataList: FieldData<T>[],
): TemplateData {
  return {
    fieldName: fieldData.fieldName,
    inputState: getVisualInputState(ctx, fieldData),
    label: fieldData.label && fieldData.label(ctx),
    tooltip: fieldData.tooltip && fieldData.tooltip(ctx),
    helpText: fieldData.helpText && fieldData.helpText(ctx.values),
    template:
      fieldData.renderField &&
      fieldData.renderField({
        ctx,
        getFormikData: (options) => formikHandlerField(ctx, fieldData, options, fieldIndex, completeDataList),
      }),
  };
}

export const MemoizedFieldWrapper = memo(
  function MemoizedFieldWrapper({
    fieldData,
    ctx,
    fieldIndex,
    completeDataList,
    override,
  }: {
    fieldData: FieldData<any>;
    ctx: FormCtxWithReviewing<any>;
    fieldIndex: number;
    completeDataList: FieldData<any>[];
    override?: (fieldData: TemplateData) => React.ReactNode;
  }) {
    const templateData = generateTemplateData(ctx, fieldData, fieldIndex, completeDataList);

    if (override) {
      return <>{override(templateData)}</>;
    }

    return <RenderDefaultFormElement {...templateData} />;
  },
  (prev, next) => {
    // Only re-render if relevant props changed
    const prevError = prev.ctx.errors[prev.fieldData.fieldName];
    const nextError = next.ctx.errors[next.fieldData.fieldName];
    const prevTouched = prev.ctx.touched[prev.fieldData.fieldName];
    const nextTouched = next.ctx.touched[next.fieldData.fieldName];
    const prevValue = prev.ctx.values[prev.fieldData.fieldName];
    const nextValue = next.ctx.values[next.fieldData.fieldName];

    return (
      prev.fieldData === next.fieldData &&
      prev.fieldIndex === next.fieldIndex &&
      prev.ctx.isReviewing === next.ctx.isReviewing &&
      prev.ctx.isSubmitting === next.ctx.isSubmitting &&
      prev.ctx.values === next.ctx.values &&
      prevError === nextError &&
      prevTouched === nextTouched &&
      prevValue === nextValue
    );
  },
);

type RenderFormElementsParams<T> = {
  ctx: FormCtxWithReviewing<T>;
  renderFieldDataList: FieldData<T>[];
  completeDataList?: FieldData<T>[];
  override?: (fieldData: TemplateData) => React.ReactNode;
};

function RenderFormElements<T>({
  ctx,
  renderFieldDataList,
  completeDataList,
  override,
}: RenderFormElementsParams<T>): React.ReactNode[] {
  // Calculate all visible fields once
  const allVisibleFields = useMemo(
    () => (completeDataList || renderFieldDataList).filter((fieldData) => fieldData.showField(ctx.values)),
    [completeDataList, renderFieldDataList, ctx.values],
  );

  const fieldIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    allVisibleFields.forEach((field, idx) => {
      // Handle duplicate field names by storing the first occurrence
      if (!map.has(field.fieldName)) {
        map.set(field.fieldName, idx);
      }
    });
    return map;
  }, [allVisibleFields]);

  // Filter renderFieldDataList to only include visible fields
  const visibleFieldsToRender = useMemo(
    () => renderFieldDataList.filter((fieldData) => allVisibleFields.includes(fieldData)),
    [renderFieldDataList, allVisibleFields],
  );

  return visibleFieldsToRender.map((fieldData) => {
    const fieldIndex = fieldIndexMap.get(fieldData.fieldName) ?? -1;

    return (
      <MemoizedFieldWrapper
        key={fieldData.fieldName}
        fieldData={fieldData}
        ctx={ctx}
        fieldIndex={fieldIndex}
        completeDataList={allVisibleFields}
        override={override}
      />
    );
  });
}

function useGroupedFields<T>(config: FieldData<T>[]) {
  return useMemo(() => {
    const grouped: Record<string, FieldData<T>[]> = {};
    for (const fieldData of config) {
      if (!grouped[fieldData.fieldName]) {
        grouped[fieldData.fieldName] = [];
      }
      grouped[fieldData.fieldName].push(fieldData);
    }
    return grouped as Record<keyof T, FieldData<T>[]>;
  }, [config]);
}

function serializeForm<T, K extends object>(ctx: FormCtx<T>, groupedFields: Record<string, FieldData<T>[]>): K {
  const serializedForm: Record<string, any> = {};

  for (const [fieldName, fields] of Object.entries(groupedFields)) {
    let value: any = undefined;

    const anyVisible = fields.some((f) => f.showField(ctx.values));

    if (anyVisible) {
      const fieldData = fields.findLast((f) => f.getInputState(ctx) !== 'disabled' && f.showField(ctx.values));

      if (fieldData) {
        value = fieldData.serializeField ? fieldData.serializeField(ctx.values) : ctx.values[fieldData.fieldName];
      }
    }

    serializedForm[fieldName] = value;
  }

  return serializedForm as K;
}

type FormProps<T extends FormikValues, Serialized extends object> = {
  config: FieldData<T>[];
  initialValues: T;
  onSubmit: (values: Serialized, ctx: FormCtx<T>) => void | Promise<any>;
  isReviewing?: boolean;
  enableReinitialize?: boolean;
};

export default function useForm<T extends FormikValues, Serialized extends object = T>({
  config,
  initialValues,
  onSubmit,
  isReviewing = false,
  enableReinitialize = false,
}: FormProps<T, Serialized>): UseFormReturn<T> {
  const [validationSchema, setValidationSchema] = useState<Yup.ObjectSchema<ObjectShape>>(Yup.object().shape({}));
  const [hasPendingData, setHasPendingData] = useState(false);
  const ctxRef = useRef<FormCtx<T> | null>(null);
  const configRef = useRef(config);
  const prevValuesRef = useRef(initialValues);

  const groupedFields = useGroupedFields(config);

  const pendingDataTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updatePendingDataDebounced = useCallback((hasChanges: boolean) => {
    if (pendingDataTimeoutRef.current) {
      clearTimeout(pendingDataTimeoutRef.current);
    }
    pendingDataTimeoutRef.current = setTimeout(() => {
      setHasPendingData(hasChanges);
    }, 300);
  }, []);

  const handleSubmit = useCallback(async () => {
    const vals = serializeForm<T, Serialized>(ctxRef.current!, groupedFields);
    const res = onSubmit(vals, ctxRef.current!);
    // if we return a promise, then isSubmitting will be handled automatically
    // https://stackoverflow.com/a/71158074
    // https://formik.org/docs/api/formik#onsubmit-values-values-formikbag-formikbag--void--promiseany
    if (typeof res === 'object' && typeof res.then === 'function') {
      await res;
      setHasPendingData(false);
      return res;
    }

    setHasPendingData(false);
    return Promise.resolve(null);
  }, [onSubmit, groupedFields]);

  const formik = useFormik({
    validationSchema,
    initialValues,
    enableReinitialize,
    onSubmit: handleSubmit,
  });

  const getSerializedValue = useCallback(
    (fieldName: keyof T) => {
      const fields = groupedFields[fieldName];
      if (!fields || fields.length === 0) {
        return formik.values[fieldName];
      }
      const fieldData = fields[fields.length - 1];

      if (!fieldData) {
        return formik.values[fieldName];
      }
      if (fieldData.serializeField) {
        return fieldData.serializeField(formik.values);
      }

      return formik.values[fieldData.fieldName];
    },
    [formik.values, groupedFields],
  );

  const stableCtx = useMemo(
    () => ({
      handleBlur: formik.handleBlur,
      handleChange: formik.handleChange,
      setFieldTouched: formik.setFieldTouched,
      setFieldValue: formik.setFieldValue,
      setValues: formik.setValues,
      setTouched: formik.setTouched,
      handleSubmit: formik.handleSubmit,
      resetForm: formik.resetForm,
    }),
    [], // These are stable references from formik
  );

  const ctx = useMemo(
    () => ({
      ...stableCtx,
      values: formik.values,
      errors: formik.errors,
      touched: formik.touched,
      isValid: formik.isValid,
      isSubmitting: formik.isSubmitting,
      isReviewing,
      hasPendingData,
      getSerializedValue,
      resetForm: formik.resetForm,
      setFieldError: formik.setFieldError,
    }),
    [
      stableCtx,
      formik.values,
      formik.errors,
      formik.touched,
      formik.isValid,
      formik.isSubmitting,
      isReviewing,
      hasPendingData,
      getSerializedValue,
    ],
  );

  useEffect(() => {
    if (!isEqual(formik.values, prevValuesRef.current)) {
      const hasChanges = !isEqual(formik.values, formik.initialValues);
      updatePendingDataDebounced(hasChanges);
      prevValuesRef.current = formik.values;
    }
  }, [formik.values, formik.initialValues, updatePendingDataDebounced]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pendingDataTimeoutRef.current) {
        clearTimeout(pendingDataTimeoutRef.current);
      }
    };
  }, []);

  const safeDescribe = (schema: Yup.ObjectSchema<ObjectShape>) => {
    try {
      return schema.describe?.() ?? {};
    } catch {
      return {};
    }
  };
  useEffect(() => {
    if (configRef.current !== config) {
      configRef.current = config;
    }
    const nextSchema = getValidationSchema(ctx, config);

    const prevShape = safeDescribe(validationSchema);
    const nextShape = safeDescribe(nextSchema);

    if (!isEqual(prevShape, nextShape)) {
      setValidationSchema(nextSchema);
    }
  }, [config]);

  useEffect(() => {
    const nextSchema = getValidationSchema(ctx, config);

    setValidationSchema(nextSchema);
  }, [ctx.values]);

  // Trigger validation when validationSchema actually changes
  useEffect(() => {
    formik.validateForm();
  }, [validationSchema]);

  ctxRef.current = ctx;

  // Memoize renderFormElements function
  const renderFormElementsMemo = useCallback(
    ({
      config: subConfig,
      override,
    }: {
      config: FieldData<T>[];
      override?: (fieldData: TemplateData) => React.ReactNode;
    }) => {
      return (
        <RenderFormElements renderFieldDataList={subConfig} completeDataList={config} ctx={ctx} override={override} />
      );
    },
    [ctx, config],
  );

  return {
    ctx,
    renderFormElements: renderFormElementsMemo,
  };
}
